import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase } from '../lib/typeHelper'

export enum Grain {
  INDICATION = 'indication',
  QUOTE = 'quote',
}

export interface QuoteAnalyticsFilters {
  grain: Grain
  granularity: 'month' | 'week'
  dateFrom?: string
  dateTo?: string
  vendors?: string[]
  states?: string[]
  fleetBuckets?: string[]
  excludeTestOrgs?: boolean // default true on backend; send 'false' to disable
}

export interface Kpis {
  total: number
  generated: number
  declined: number
  unavailable: number
  errored: number
  pending: number
  canceled: number
  declineRate: number
  totalPremium: number
  avgPremium: number
}

export interface VendorRow {
  vendor: string
  carrier: string
  generated: number
  declined: number
  unavailable: number
  error: number
  premium: number
}
export interface StateRow {
  state: string
  generated: number
  declined: number
  unavailable: number
  error: number
  premium: number
}
export interface FleetRow {
  bucket: string
  generated: number
  declined: number
  unavailable: number
  error: number
  premium: number
}
export interface MonthRow {
  period: string
  generated: number
  declined: number
  unavailable: number
  error: number
  premium: number
}
export interface DayRow {
  period: string
  generated: number
  declined: number
  unavailable: number
  error: number
  premium: number
}
export interface VendorDaySeries {
  vendor: string
  name: string
  days: DayRow[]
}
export interface Funnel {
  estimates: number
  indications: number
  quotes: number
  bound: number
}
/** Time on page from a customer starting their application to their first priced quote; null with no applications. */
export interface TimeToQuoting {
  applications: number
  medianTimeOnPageSeconds: number | null
  p90TimeOnPageSeconds: number | null
  medianElapsedSeconds: number | null
}
export interface DeclineRow {
  vendor: string
  reason: string
  count: number
}
export interface WinRateRow {
  vendor: string
  carrier: string
  quotes: number
  bound: number
  winRate: number
  avgHoursToReturn: number | null
}
export interface AccuracyPoint {
  vendor: string
  carrier: string
  estimateMid: number
  actual: number
}
export interface Accuracy {
  sampleSize: number
  withinRangeRate: number
  medianVariancePct: number
  byVendor: { vendor: string; sampleSize: number; withinRangeRate: number; medianVariancePct: number }[]
  points: AccuracyPoint[]
}

export interface QuoteAnalyticsSummary {
  grain: Grain
  granularity: 'month' | 'week'
  kpis: Kpis
  byVendor: VendorRow[]
  byState: StateRow[]
  byFleet: FleetRow[]
  byMonth: MonthRow[]
  byVendorDay: VendorDaySeries[]
  funnel: Funnel
  timeToQuoting: TimeToQuoting
  declineReasons: DeclineRow[]
  winRate: WinRateRow[]
  premiumDistribution: {
    byState: { state: string; avgPerUnit: number; medianPerUnit: number }[]
    byFleet: { bucket: string; avgPerUnit: number; medianPerUnit: number }[]
  }
  accuracy: Accuracy
}

export interface OfferListRow {
  applicationId: string
  vendor: string
  carrier: string
  state: string
  fleetBucket: string
  fleetSize: number
  status: string
  premium: number
  month: string
  dotNumber: string | null
  declineReason: string
}

const BASE = '/api/v1/insure/internal/quote-analytics'

function toQuery(f: QuoteAnalyticsFilters): URLSearchParams {
  const q = new URLSearchParams({ grain: f.grain, granularity: f.granularity })
  if (f.dateFrom) q.set('date_from', f.dateFrom)
  if (f.dateTo) q.set('date_to', f.dateTo)
  if (f.vendors?.length) q.set('vendor', f.vendors.join(','))
  if (f.states?.length) q.set('state', f.states.join(','))
  if (f.fleetBuckets?.length) q.set('fleet_bucket', f.fleetBuckets.join(','))
  // Only send when overriding the backend default (true → omit; false → send)
  if (f.excludeTestOrgs === false) q.set('exclude_test_orgs', 'false')
  return q
}

export function useQuoteAnalyticsSummary(filters: QuoteAnalyticsFilters) {
  return useQuery({
    queryKey: [CacheKeys.QUOTE_ANALYTICS, 'summary', filters],
    queryFn: () =>
      axios
        .get(`${BASE}/summary?${toQuery(filters).toString()}`)
        .then((r) => toCamelCase(r.data) as QuoteAnalyticsSummary),
  })
}

export function useQuoteAnalyticsQuotes(filters: QuoteAnalyticsFilters, page: number) {
  const q = toQuery(filters)
  if (page > 1) q.set('page', String(page))
  return useQuery({
    queryKey: [CacheKeys.QUOTE_ANALYTICS, 'quotes', filters, page],
    queryFn: () =>
      axios
        .get(`${BASE}/quotes?${q.toString()}`)
        .then((r) => toCamelCase(r.data) as PaginatedResults<OfferListRow> & { page: number }),
  })
}
